import { Button, Empty, Flex, Skeleton, Spin } from "antd"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { type RootState } from "../../../store"
import { NotificationSettingsForm } from "../../Notifications/components/NotificationSettingsForm"
import {
  fetchNotificationSettings,
  updateNotificationSettings
} from "../../Notifications/redux/notifications.thunks"
import { SmsAlertsNotice } from "../../Notifications/sms/SmsAlertsNotice.tsx"
import { useSmsVerification } from "../../Notifications/sms/use-sms-verification.ts"
import type {
  NotificationSettings,
  NotificationSettingsItem
} from "../../Notifications/types/notification.types"

// CALENDAR moved to Calendar Settings > Notifications, which reads and
// writes this exact same group through the same thunks/endpoint — one
// source of truth, shown in one place. Hiding it here only drops it from
// this form's render; a save from here still can't touch it, since the form
// never registers `Form.Item`s for a group it was never given.
const GLOBAL_PAGE_EXCLUDED_GROUPS = ["CALENDAR"]

const forGlobalPage = (
  settings: NotificationSettings | null
): NotificationSettings | null => {
  if (!settings) return settings
  return Object.fromEntries(
    Object.entries(settings).filter(
      ([key]) => !GLOBAL_PAGE_EXCLUDED_GROUPS.includes(key)
    )
  )
}

const SettingsNotificationPage = ({
  data,
  settingsError,
  getSettings,
  isUpdating,
  updateSettings
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const sms = useSmsVerification()

  useEffect(() => {
    getSettings()
  }, [])

  const handleFinish = (values: {
    [key: string]: NotificationSettingsItem
  }) => {
    // Backend 500s on a partial row (reads all channel fields with no
    // default); the form registers every CHANNEL_KEYS field per row, so
    // spreading `value` always sends the full row.
    const mapValues = Object.entries(values).map(([key, value]) => ({
      ...value,
      notification_type: Number(key)
    }))
    updateSettings(mapValues)
  }

  useEffect(() => {
    generateDocumentTitle("Settings Notifications")
  }, [])
  return (
    <>
      {data ? (
        <Flex vertical gap={12} flex={1}>
          {Object.values(data)
            .flat()
            .some((item) => item.sms_available === true) && (
            <SmsAlertsNotice
              state={sms.state}
              lock={sms.lock}
              failed={sms.failed}
              onRetry={() => void sms.reload()}
              onVerified={(verified) => {
                sms.setState(verified)
                getSettings()
              }}
            />
          )}
          <NotificationSettingsForm
            settings={data}
            onFinish={handleFinish}
            saving={isUpdating}
            smsLock={sms.lock}
          />
        </Flex>
      ) : settingsError ? (
        <Flex flex={1} align="center" justify="center">
          <Empty description={t("notifications.settings.loadError")}>
            <Button onClick={() => getSettings()}>
              {t("notifications.settings.retry")}
            </Button>
          </Empty>
        </Flex>
      ) : (
        <Flex flex={1}>
          <Spin fullscreen size="large" />
          <Skeleton.Node className={"size-full"} />
        </Flex>
      )}
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  data: forGlobalPage(state.notification.notificationSettings),
  fetching: state.notification.fetchingSettings,
  settingsError: state.notification.settingsError,
  isUpdating: state.notification.isUpdatingSettings
})

const mapDispatchToProps = {
  getSettings: fetchNotificationSettings,
  updateSettings: updateNotificationSettings
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(SettingsNotificationPage)
